<template>
  <modal-wrapper
    ref="modalRef"
    :busy="isBusy"
    :close-on-ok-click="false"
    :header="t('documentDetailView.updateDocumentTitle')"
    :ok-button-label="t('documentDetailView.updateDocument')"
    :size="ModalSize.L"
    @ok-button-click="handleSubmit"
    @cancel-button-click="resetState"
  >
    <div class="workspace-document-update-modal">
      <p
        v-if="!isLoadingUpdateInfo"
        class="workspace-document-update-modal__hint"
      >
        {{
          hasRenditionFile
            ? t('documentDetailView.updateDocumentWithRenditionHint')
            : t('documentDetailView.updateDocumentWithoutRenditionHint')
        }}
      </p>

      <single-upload-file-inputs
        :key="fileInputsKey"
        :is-uploading="isBusy"
        :upload-progress="uploadProgress"
        :show-rendition-input="hasRenditionFile"
        @main-file-selected="handleMainFileSelected"
        @rendition-file-selected="handleRenditionFileSelected"
      />

      <message-wrapper v-if="isSubmitted && !mainFile" simple-error>
        {{ t('documentDetailView.errors.mainFileRequired') }}
      </message-wrapper>
      <message-wrapper
        v-if="isSubmitted && hasRenditionFile && !renditionFile"
        simple-error
      >
        {{ t('documentDetailView.errors.renditionFileRequired') }}
      </message-wrapper>
    </div>
  </modal-wrapper>
</template>

<script setup lang="ts">
  import { ModalSize, ModalWrapper } from '@/components/ModalWrapper';
  import MessageWrapper from '@/components/MessageWrapper/MessageWrapper.vue';
  import SingleUploadFileInputs from '@/components/SingleUploadFileInputs/SingleUploadFileInputs.vue';
  import {
    useWorkspaceDocumentUpdate,
    useWorkspaceDocumentUpdateInfo,
  } from '@/composables/api/workspaces';
  import { useToast } from '@/composables/useToast';
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  const props = defineProps<{
    workspaceId: string;
    documentId: string;
  }>();

  const emit = defineEmits<{
    (e: 'updated'): void;
  }>();

  const { t } = useI18n();
  const { createErrorToast, createSuccessToast } = useToast();
  const { updateWorkspaceDocumentFile, uploadProgress } =
    useWorkspaceDocumentUpdate();
  const { getWorkspaceDocumentUpdateInfo } = useWorkspaceDocumentUpdateInfo();

  const modalRef = ref<InstanceType<typeof ModalWrapper> | null>(null);
  const hasRenditionFile = ref(false);
  const isLoadingUpdateInfo = ref(false);
  const isSubmitting = ref(false);
  const isSubmitted = ref(false);
  const mainFile = ref<File | null>(null);
  const renditionFile = ref<File | null>(null);
  const fileInputsKey = ref(0);

  const isBusy = computed(
    () => isLoadingUpdateInfo.value || isSubmitting.value,
  );

  defineExpose({
    show,
    hide,
  });

  async function show(): Promise<void> {
    resetState();
    modalRef.value?.show();

    isLoadingUpdateInfo.value = true;
    try {
      const response = await getWorkspaceDocumentUpdateInfo({
        workspaceId: props.workspaceId,
        documentId: props.documentId,
      });

      hasRenditionFile.value = response.hasRenditionFile;
    } catch {
      hide();
      createErrorToast(t('documentDetailView.errors.cannotLoadUpdateInfo'));
    } finally {
      isLoadingUpdateInfo.value = false;
    }
  }

  function hide(): void {
    modalRef.value?.hide();
    resetState();
  }

  function resetState(): void {
    hasRenditionFile.value = false;
    isLoadingUpdateInfo.value = false;
    isSubmitting.value = false;
    isSubmitted.value = false;
    mainFile.value = null;
    renditionFile.value = null;
    fileInputsKey.value += 1;
  }

  function handleMainFileSelected(file: File): void {
    mainFile.value = file;
  }

  function handleRenditionFileSelected(file: File): void {
    renditionFile.value = file;
  }

  async function handleSubmit(): Promise<void> {
    isSubmitted.value = true;

    if (!mainFile.value) {
      return;
    }

    if (hasRenditionFile.value && !renditionFile.value) {
      return;
    }

    isSubmitting.value = true;
    try {
      await updateWorkspaceDocumentFile({
        workspaceId: props.workspaceId,
        documentId: props.documentId,
        mainFile: mainFile.value,
        renditionFile: renditionFile.value ?? undefined,
      });
      createSuccessToast(t('documentDetailView.updateDocumentSuccessful'));
      hide();
      emit('updated');
    } catch {
      createErrorToast(t('documentDetailView.errors.cannotUpdateDocument'));
    } finally {
      isSubmitting.value = false;
    }
  }
</script>

<style scoped lang="scss">
  .workspace-document-update-modal {
    display: flex;
    flex-direction: column;
    row-gap: 1rem;

    &__hint {
      margin: 0;
    }
  }
</style>
